// lib/services/animation/joint_attachment.ts

import Attachment from "./attachment";
import { ModelData } from "../geometry/modeldata";
import AnimationClip from "./animation_clip";
import AnimationPoseSampler from "./animation_pose_sampler";
import AnimationControllerGpu from "./animation_controller_gpu";
import {
  identityMat4,
  multiplyMat4,
  trsToMat4,
} from "./anim_math";

export default class JointAttachment extends Attachment {
  readonly joint_index: number;

  private model: ModelData | null = null;
  private skin_index: number = -1;
  private mesh_node_index: number = -1;
  private ancestor_chain: number[] = [];
  private mesh_ancestor_chain: number[] = [];

  constructor(
    name: string,
    joint_index: number,
    local_matrix?: Float32Array
  ) {
    super(name, local_matrix);
    this.joint_index = joint_index;
  }

  bind(model: ModelData, skin_index: number, mesh_node_index: number): void {
    this.model = model;
    this.skin_index = skin_index;
    this.mesh_node_index = mesh_node_index;

    const skin = model.skins[skin_index];
    const node_index = skin.joints[this.joint_index];

    this.ancestor_chain = this.build_ancestor_chain(node_index);
    this.mesh_ancestor_chain = this.build_ancestor_chain(mesh_node_index);
  }

  update(parent: any): void {
  if (!this.model) return;

  const controller = parent.animation_controller_gpu as AnimationControllerGpu | null;
  if (!controller) return;

  const raw = controller.get_internal_state();
  if (!raw.current) return;

  const state: InternalAnimState = {
    current: raw.current,
    next: raw.next,
    blendAlpha: raw.blendAlpha,
  };

  const joint_global = this.compose_global_for_chain(
    this.ancestor_chain,
    state
  );

  const parent_model_matrix = parent.transform.model_matrix as Float32Array;
  const world_joint = multiplyMat4(parent_model_matrix, joint_global);

  this.apply_world_matrix(world_joint);
}

  private compose_global_for_chain(
    chain: number[],
    state: InternalAnimState
  ): Float32Array {
    let global = identityMat4();

    for (let i = chain.length - 1; i >= 0; i--) {
      const node_index = chain[i];
      const trs = this.resolve_blended_trs(node_index, state);
      const local = trsToMat4(trs.t, trs.r, trs.s);
      global = multiplyMat4(global, local);
    }

    return global;
  }

  
  private resolve_blended_trs(
  node_index: number,
  state: InternalAnimState
): { t: Float32Array; r: Float32Array; s: Float32Array } {
  const current_trs = this.sample_node_trs(
    node_index,
    state.current.clip,
    state.current.time,
    state.current.loop
  );

  if (!state.next) {
    if (node_index === state.current.clip.rootMotionNodeIndex) {
      current_trs.t = new Float32Array([0, 0, 0]);
    }
    return current_trs;
  }

  const next_trs = this.sample_node_trs(
    node_index,
    state.next.clip,
    state.next.time,
    state.next.loop
  );

  const alpha = state.blendAlpha;

  const result = {
    t: AnimationPoseSampler.lerpVec3(current_trs.t, next_trs.t, alpha),
    r: AnimationPoseSampler.nlerpQuatShortest(
      current_trs.r,
      next_trs.r,
      alpha
    ),
    s: AnimationPoseSampler.lerpVec3(current_trs.s, next_trs.s, alpha),
  };

  const active_clip = alpha < 0.5 ? state.current.clip : state.next.clip;
  if (node_index === active_clip.rootMotionNodeIndex) {
    result.t = new Float32Array([0, 0, 0]);
  }

  return result;
}

  private sample_node_trs(
    node_index: number,
    clip: AnimationClip,
    time: number,
    loop: boolean
  ): { t: Float32Array; r: Float32Array; s: Float32Array } {
    if (!this.model) {
      return {
        t: new Float32Array([0, 0, 0]),
        r: new Float32Array([0, 0, 0, 1]),
        s: new Float32Array([1, 1, 1]),
      };
    }

    const node = this.model.nodes[node_index];

    let t = new Float32Array(node.translation);
    let r = new Float32Array(node.rotation);
    let s = new Float32Array(node.scale);

    for (const channel of clip.channels) {
      if (channel.nodeIndex !== node_index) continue;

      const sample = AnimationPoseSampler.sampleChannel(
        channel,
        time,
        clip.duration,
        loop
      );
      if (!sample) continue;

      switch (channel.path) {
        case "translation":
            t = new Float32Array(sample);
            break;
        case "rotation":
            r = new Float32Array(sample);
            AnimationPoseSampler.normalizeQuat(r);
            break;
        case "scale":
            s = new Float32Array(sample);
            break;
}
    }

    return { t, r, s };
  }

  private build_ancestor_chain(node_index: number): number[] {
    if (!this.model) return [];

    const chain: number[] = [];
    let current = node_index;

    while (current >= 0) {
      chain.push(current);
      current = this.model.nodes[current].parentIndex;
    }

    return chain;
  }
}

type InternalAnimState = {
  current: { clip: AnimationClip; time: number; loop: boolean };
  next: { clip: AnimationClip; time: number; loop: boolean } | null;
  blendAlpha: number;
};